import { AuthContext } from "../Context/AuthContext";
import { toast } from "react-toastify";
import { TextField, Button, Grid, Dialog, DialogTitle, DialogContent, DialogContentText, Box, Typography } from "@mui/material";
import { useEffect, useState, useContext } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from '../utils/axiosConfig';
import Navbar from "../components/Navbar";
import CountryPhoneInput from "../components/CountryPhoneInput";

function SSOOnboarding() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(true); // Show dialog on mount
  const { setIsAuthenticated, setUser, setToken } = useContext(AuthContext);

  const [params] = useSearchParams();
  const { state } = useLocation();
  const navigateTo = useNavigate();

  useEffect(() => {
    // Pre-fill form with data from SSO if available
    if (state) {
      if (state.firstName) setFirstName(state.firstName);
      if (state.lastName) setLastName(state.lastName);
    }
  }, [state]);

  // Watch phone field and control the dialog visibility
  useEffect(() => {
    // Show dialog if phone is empty
    if (!phone || phone.trim() === "") {
      setShowPhoneDialog(true);
    }
  }, [phone]);

  useEffect(() => {
    // Ensure this is only accessed for SSO flows
    const appleSub = params.get("appleSub");
    const isGoogleSSO = state?.ssoType === "google" || (!!state?.email && !state?.appleSub); // Google SSO: has email but no appleSub
    const isAppleSSO = state?.ssoType === "apple" || !!state?.appleSub || !!appleSub; // Apple SSO: has appleSub in state or params
    const isSSO = isGoogleSSO || isAppleSSO;
    
    console.log("SSO Detection:", {
      appleSub,
      isGoogleSSO,
      isAppleSSO,
      isSSO,
      ssoType: state?.ssoType,
      stateEmail: state?.email,
      stateAppleSub: state?.appleSub
    });
    
    if (!isSSO) {
      console.log("Not an SSO flow, redirecting to login");
      navigateTo("/login");
    }
  }, [params, state, navigateTo]);

  async function handleOnboardingSubmit(e) {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Debug logging
      console.log("=== SSO ONBOARDING DEBUG ===");
      console.log("Form data:", { firstName, lastName, phone });
      console.log("URL params appleSub:", params.get("appleSub"));
      console.log("State data:", state);
      console.log("State email:", state?.email);
      console.log("State appleSub:", state?.appleSub);
      console.log("SSO Type:", state?.ssoType);

      // Validate required fields
      if (!firstName) {
        toast.error("First name is required!");
        setLoading(false);
        return;
      }
      if (!lastName) {
        toast.error("Last name is required!");
        setLoading(false);
        return;
      }
      if (!phone) {
        toast.error("Phone number is required!");
        setLoading(false);
        return;
      }

      // Prepare API body for both Google and Apple SSO
      const api_body = {
        firstName: firstName?.trim(),
        lastName: lastName?.trim(),
        phone: phone?.trim()
      };

      console.log("[SSO Onboarding] Phone with country code:", phone);

      // Add identifier based on SSO type
      const appleSub = params.get("appleSub") || state?.appleSub;
      if (appleSub) {
        api_body.appleSub = appleSub;
      } else if (state?.email) {
        api_body.email = state.email?.trim();
      }

      console.log("Final API body:", api_body);

      const response = await axiosInstance.post(
        "/user/complete-onboarding?token=true", 
        api_body
      );

      const data = response.data;
      
      // Debug: log backend response
      console.log("Onboarding response:", data);

      if (data.success) {
        toast.success(data.message || "Onboarding completed successfully!");
        
        // Set authentication state
        if (data.token && data.user) {
          setIsAuthenticated(true);
          setUser(data.user);
          setToken(data.token);
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          if (data.expiresAt) {
            localStorage.setItem('tokenExpiresAt', data.expiresAt);
          }
        }
        
        // Navigate to dashboard/home
        navigateTo("/");
      } else {
        toast.error(data.message || "Onboarding failed");
      }
    } catch (error) {
      console.error("=== ONBOARDING ERROR DEBUG ===");
      console.error("Full error object:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      console.error("Error message:", error.message);
      console.error("Error status:", error.response?.status);
      
      const errorMessage = error.response?.data?.message || error.message || "An error occurred during onboarding";
      toast.error(errorMessage);
      
      // If it's a 404 or 400 error, might be a configuration issue
      if (error.response?.status === 404) {
        console.error("❌ 404 Error - Check if the backend route '/complete-onboarding' exists");
      } else if (error.response?.status === 400) {
        console.error("❌ 400 Error - Check if required data is being sent correctly");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Navbar />
      
      {/* Persistent Phone Number Dialog */}
      <Dialog 
        open={showPhoneDialog} 
        onClose={() => {}} // Prevent closing by clicking outside
        disableEscapeKeyDown // Prevent closing with Escape key
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { overflow: 'visible', overflowY: 'visible' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
          Phone Number Required
        </DialogTitle>
        <DialogContent sx={{ overflow: 'visible', overflowY: 'visible' }}>
          <Box sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 2 }}>
              Your phone number is required to complete your profile and access all features of the application.
            </DialogContentText>
            <CountryPhoneInput
              value={phone}
              onChange={setPhone}
              label="Mobile Number"
              autoFocus
            />
            <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary"
                disabled={!phone || phone.trim() === ""}
                onClick={() => {
                  if (phone && phone.trim() !== "") {
                    setShowPhoneDialog(false);
                  } else {
                    toast.error("Please enter a valid phone number");
                  }
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <div className="py-8 flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 min-h-screen">
        <div className="w-full max-w-md space-y-2">
          {/* Title Section */}
          <div className="text-center">
            <h2 className="text-3xl font-semibold text-gray-900 mb-2">
              Complete Your Profile
            </h2>
            <p className="text-gray-600">
              Please provide additional information to complete your registration
            </p>
          </div>

          {/* Onboarding Form Card */}
          <div className="bg-white py-8 px-4 shadow-2xl rounded-2xl space-y-8 sm:px-10">
            <form onSubmit={handleOnboardingSubmit} className="space-y-6">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="First Name" 
                    variant="outlined" 
                    margin="normal" 
                    value={firstName} 
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Last Name" 
                    variant="outlined" 
                    margin="normal" 
                    value={lastName} 
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CountryPhoneInput
                    value={phone}
                    onChange={setPhone}
                    required
                    disabled={loading}
                    label="Mobile Number"
                  />
                </Grid>
              </Grid>
              <Grid container justifyContent="center">
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  disabled={loading}
                  style={{ marginTop: "20px" }} 
                  className="w-full py-3 rounded-full text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {loading ? "Completing..." : "Complete Registration"}
                </Button>
              </Grid>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default SSOOnboarding;
